/**
 * Maps AI-detected grass types to standardized lawn profile dropdown values.
 * The AI may return various names/variations that need to map to our standard list.
 */

// Standard grass types used in lawn profile dropdowns
export const SUPPORTED_GRASS_TYPES = [
  'Kentucky Bluegrass',
  'Bermuda Grass',
  'St. Augustine',
  'Zoysia',
  'Fescue',
  'Ryegrass',
  'Centipede Grass',
  'Bahia Grass',
  'Kikuyugrass',
  'Annual Ryegrass',
  'Mixed Cool-Season Blend',
  'Carpetgrass',
] as const

export type SupportedGrassType = typeof SUPPORTED_GRASS_TYPES[number]

// AI grass type variations -> Standard lawn profile type
const GRASS_TYPE_MAP: Record<string, SupportedGrassType> = {
  'Carpetgrass': 'Carpetgrass',
  'Carpet Grass': 'Carpetgrass',
  'Common Carpetgrass': 'Carpetgrass',
  'Axonopus fissifolius': 'Carpetgrass',
  'Axonopus affinis': 'Carpetgrass',
  // Kentucky Bluegrass variants
  'Kentucky Bluegrass': 'Kentucky Bluegrass',
  'Kentucky Blue Grass': 'Kentucky Bluegrass',
  'KBG': 'Kentucky Bluegrass',
  'Bluegrass': 'Kentucky Bluegrass',

  // Bermuda variants
  'Bermuda Grass': 'Bermuda Grass',
  'Bermuda': 'Bermuda Grass',
  'Bermudagrass': 'Bermuda Grass',
  'Common Bermuda': 'Bermuda Grass',
  'Hybrid Bermuda': 'Bermuda Grass',

  // St. Augustine variants
  'St. Augustine': 'St. Augustine',
  'St Augustine': 'St. Augustine',
  'Saint Augustine': 'St. Augustine',
  'St. Augustine Grass': 'St. Augustine',
  'Floratam': 'St. Augustine',

  // Zoysia variants
  'Zoysia': 'Zoysia',
  'Zoysia Grass': 'Zoysia',
  'Zoysiagrass': 'Zoysia',
  'Emerald Zoysia': 'Zoysia',
  'Meyer Zoysia': 'Zoysia',

  // Fescue variants
  'Fescue': 'Fescue',
  'Tall Fescue': 'Fescue',
  'Fine Fescue': 'Fescue',
  'Creeping Red Fescue': 'Fescue',
  'Hard Fescue': 'Fescue',
  'Chewings Fescue': 'Fescue',
  'Turf Type Tall Fescue': 'Fescue',
  'TTTF': 'Fescue',

  // Ryegrass variants
  'Ryegrass': 'Ryegrass',
  'Perennial Ryegrass': 'Ryegrass',
  'Rye Grass': 'Ryegrass',

  // Annual Ryegrass variants (winter overseed; distinct from the perennial bucket)
  'Annual Ryegrass': 'Annual Ryegrass',
  'Annual Rye': 'Annual Ryegrass',
  'Annual Rye Grass': 'Annual Ryegrass',
  'Italian Ryegrass': 'Annual Ryegrass',
  'Italian Rye': 'Annual Ryegrass',
  'Winter Rye': 'Annual Ryegrass',
  'Winter Ryegrass': 'Annual Ryegrass',

  // Centipede variants
  'Centipede Grass': 'Centipede Grass',
  'Centipede': 'Centipede Grass',
  'Centipedegrass': 'Centipede Grass',

  // Bahia variants
  'Bahia Grass': 'Bahia Grass',
  'Bahia': 'Bahia Grass',
  'Bahiagrass': 'Bahia Grass',
  'Pensacola Bahia': 'Bahia Grass',
  'Argentine Bahia': 'Bahia Grass',

  // Kikuyugrass variants
  'Kikuyugrass': 'Kikuyugrass',
  'Kikuyu': 'Kikuyugrass',
  'Kikuyu Grass': 'Kikuyugrass',
  'Pennisetum Clandestinum': 'Kikuyugrass',
  'Cenchrus Clandestinus': 'Kikuyugrass',

  // Mixed cool-season blend variants
  'Mixed Cool-Season Blend': 'Mixed Cool-Season Blend',
  'Mixed Cool Season Blend': 'Mixed Cool-Season Blend',
  'Cool-Season Mix': 'Mixed Cool-Season Blend',
  'Cool Season Mix': 'Mixed Cool-Season Blend',
  'Cool-Season Blend': 'Mixed Cool-Season Blend',
  'Cool Season Blend': 'Mixed Cool-Season Blend',
  'KBG/Rye/Fescue Mix': 'Mixed Cool-Season Blend',
  'Cool-Season Lawn Mix': 'Mixed Cool-Season Blend',
}

/**
 * Maps an AI-detected grass type to a standard lawn profile type.
 *
 * @param aiGrassType - The grass type string returned by the AI analysis
 * @returns The standardized grass type, or null if no match found
 *
 * @example
 * mapGrassTypeToLawnProfile('Tall Fescue') // => 'Fescue'
 * mapGrassTypeToLawnProfile('Bermudagrass') // => 'Bermuda Grass'
 * mapGrassTypeToLawnProfile('Unknown Grass') // => null
 */
export function mapGrassTypeToLawnProfile(aiGrassType: string): SupportedGrassType | null {
  if (!aiGrassType) return null

  // Direct lookup (exact match)
  const directMatch = GRASS_TYPE_MAP[aiGrassType]
  if (directMatch) {
    return directMatch
  }

  // Case-insensitive lookup
  const normalizedInput = aiGrassType.toLowerCase().trim()
  for (const [key, value] of Object.entries(GRASS_TYPE_MAP)) {
    if (key.toLowerCase() === normalizedInput) {
      return value
    }
  }

  // Fuzzy match - check if input contains or is contained by a known type
  if (/carpet\s*grass|axonopus/.test(normalizedInput)) return null
  for (const [key, value] of Object.entries(GRASS_TYPE_MAP)) {
    const normalizedKey = key.toLowerCase()
    // A common name substring must not collapse other Axonopus species.
    if (value === 'Carpetgrass') continue
    if (normalizedInput.includes(normalizedKey) || normalizedKey.includes(normalizedInput)) {
      return value
    }
  }

  // Check if input matches any of the standard types directly
  for (const standardType of SUPPORTED_GRASS_TYPES) {
    if (standardType === 'Carpetgrass') continue
    if (normalizedInput.includes(standardType.toLowerCase()) ||
        standardType.toLowerCase().includes(normalizedInput)) {
      return standardType
    }
  }

  return null
}

/**
 * Checks if a grass type is in our supported list.
 */
export function isSupportedGrassType(grassType: string): grassType is SupportedGrassType {
  return SUPPORTED_GRASS_TYPES.includes(grassType as SupportedGrassType)
}
